import React from 'react'
import PropTypes from 'prop-types'
import cssModules from 'react-css-modules'
import CrossFadeTransition from 'components/common/transitions/cross_fade'
import styles from './send_review.less'
import Alert from 'components/common/alert'
import Avatar from 'components/common/avatar'
import Money from 'components/common/money'
import Separator from 'components/common/separator'
import { getI18n } from 'client/i18n'
import { generateFF as generateSendURL } from 'shared/send_money_url_generator'
import SendSuccess from 'components/glimpses/send_glimpse/send_success'
import escapeHTML from 'escape-html'
import { withRouter } from 'react-router'
import { routerShape } from 'react-router/lib/PropTypes'
import { decodeCampaignId } from 'shared/campaign_url'
import { sendEnsightenEvent } from 'lib/ensighten_analytics'

const IFRAME_HEIGHT_SET_INTERVAL = 50
const i18n = getI18n('components/send_glimpse/send_review')

class SendReview extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    ownerDisplayName: PropTypes.string,
    currency: PropTypes.string,
    campaignId: PropTypes.string,
    loadingIFrame: PropTypes.bool,
    loadingDone: PropTypes.bool,
    sendDone: PropTypes.bool,
    iFrameError: PropTypes.string,
    gsBypass: PropTypes.bool,

    amount: PropTypes.number,
    anonymous: PropTypes.bool,
    note: PropTypes.string,

    onInit: PropTypes.func,
    onIframeMessage: PropTypes.func,
    router: routerShape,
  }

  state = { goingBack: false }

  componentDidMount() {
    this.iframeHeight = 0 // not handled in state since the frequent state changes mess up transitions
    window.addEventListener('message', this.handleIframeMessage)
    this.iframeHeightInterval = setInterval(
      this.setIframeHeight,
      IFRAME_HEIGHT_SET_INTERVAL
    )
    this.props.onInit()
  }

  componentWillUnmount() {
    clearInterval(this.iframeHeightInterval)
    window.removeEventListener('message', this.handleIframeMessage)
  }

  parseIFramePayload = payload => {
    try {
      return JSON.parse(payload)
    } catch (e) {
      return {}
    }
  }

  handleIframeMessage = payload => {
    payload = {
      campaignId: this.props.campaignId,
      ...this.parseIFramePayload(payload.data),
    }
    const eventName = payload.statusMessage || payload.event
    if (eventName === 'iframeReady') {
      this.disableIframeBack()
    }
    if (eventName === 'successPageRendered') {
      sendEnsightenEvent('MoneyPoolSendFin')
    }
    this.props.onIframeMessage(eventName, payload)
  }

  // since 8ball is an iframe, it will capture the back button.
  // this prevents it from doing so.
  disableIframeBack = () => {
    this.iframe.contentWindow.onpopstate = () => {
      // ugly hack that prevents the back from happening in the iframe.
      // remove the iframe first, then perform the back - otherwise the iframe always steal the back.
      // `goingBack: true` means the iframe won't be rendered.
      // this might cause a full page redirect though, which is an okay solution for this bug.
      // (battle-tested in all browsers)
      this.setState({ goingBack: true }, () => this.props.router.goBack())
    }
  }

  getIFrameDocument = () => {
    return (
      this.iframe &&
      (this.iframe.contentDocument || this.iframe.contentWindow.document)
    )
  }

  setIframeHeight = () => {
    const iframeDocument = this.getIFrameDocument()
    if (!iframeDocument) return

    const transferPage =
      iframeDocument.querySelector('#react-transfer-container') || {}
    const overpanels =
      iframeDocument.querySelectorAll('[data-pools-iframe-key]') || []
    const overpanelHeights =
      overpanels.length > 0 ? [...overpanels].map(el => el.clientHeight) : []
    // spread because the dom function doesn't really return an array
    const maxOverpanelHeight =
      overpanelHeights.length > 0 ? Math.max(...overpanelHeights) : 0
    const pageHeight = parseInt(transferPage.clientHeight, 10) || 0
    let iframeHeight = maxOverpanelHeight > 0 ? maxOverpanelHeight : pageHeight
    this.iframeHeight = iframeHeight
    this.iframe.setAttribute('height', iframeHeight)
  }

  render() {
    const { amount, anonymous, note, campaignId } = this.props
    const sendURL = generateSendURL(
      {
        amount: amount,
        currencyCode: this.props.currency,
        anon: anonymous,
        note: note,
        campaign: decodeCampaignId(campaignId),
      },
      this.props.gsBypass
    )
    const opacity = this.props.loadingDone ? 1 : 0
    return (
      <div styleName="send-review" style={{ opacity }}>
        <CrossFadeTransition>
          {this.props.sendDone ? (
            <SendSuccess campaignId={campaignId} key="success" />
          ) : (
            <div styleName="review-info-container" key="review">
              <div styleName="alert-container">
                {this.props.iFrameError && (
                  <Alert type="critical">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: this.props.iFrameError,
                      }}
                    />
                  </Alert>
                )}
              </div>
              <div styleName="avatar-container">
                <Avatar campaignId={campaignId} />
              </div>
              <div
                styleName="recipient"
                dangerouslySetInnerHTML={{
                  __html: i18n('sending', {
                    name: escapeHTML(this.props.ownerDisplayName),
                  }),
                }}
              />
              <Separator type="dotted" />
              <div styleName="money-container">
                <Money
                  size="big"
                  editable={false}
                  removeTrailingZeros={false}
                  currency={this.props.currency}
                  value={amount}
                />
              </div>
              {note && (
                <div styleName="note" data-nemo="note">
                  {this.props.note}
                </div>
              )}
              <Separator type="dotted" />
              <div styleName="for">{i18n('for')}</div>
              <div styleName="event-title">{this.props.title}</div>
              <Separator type="dotted" />
            </div>
          )}
        </CrossFadeTransition>
        {!this.state.goingBack && (
          <iframe
            id="p2p-iframe"
            src={sendURL}
            styleName="send-iframe"
            ref={iframe => {
              this.iframe = iframe
            }}
            height={this.iframeHeight}
          />
        )}
      </div>
    )
  }
}

export default withRouter(cssModules(SendReview, styles))
